/** @format */

import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID, makeStateKey, TransferState } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { map, retry } from 'rxjs/operators';

import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';

import { DataFiles, Environment } from '@shared/data-access/interfaces';

@Injectable({
  providedIn: 'root',
})
export class FileDao {
  readonly FILE_STATE_KEY_PREFIX = 'file-';

  private readonly environment = inject(Environment);
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly state = inject(TransferState);

  /**
   * @description
   * Gets a data file by name and type
   */
  public getDataFileByType$<T = unknown>(
    fileName: string,
    fileType: keyof DataFiles,
    retries = 0,
  ): Observable<T> {
    // Get unique key
    const STATE_KEY = makeStateKey<T>(`${this.FILE_STATE_KEY_PREFIX}${fileName}`);

    // Attempt to get the state by key
    const stateData = this.state.get(STATE_KEY, null as T | null);

    // Check to see if state exists
    if (stateData !== null && stateData !== undefined) {
      // Return the existing state
      return new Observable<T>((observer) => {
        observer.next(stateData);
        observer.complete();
      });
    }

    const dataFiles = this.environment.dataFiles;
    if (!dataFiles) {
      return throwError(() => new Error('Data file configuration is unavailable.'));
    }

    // Standard HTTP Headers
    let httpHeaders = new HttpHeaders();

    // Check file type to specify `Accept` header value
    /* istanbul ignore else */
    if (typeof fileType === 'string' && fileType.toLowerCase().trim() === 'json') {
      httpHeaders = httpHeaders.set('Accept', MediaType.APPLICATION_JSON);
    } else if (typeof fileType === 'string' && fileType.toLowerCase().trim() === 'csv') {
      httpHeaders = httpHeaders.set('Accept', MediaType.TEXT_CSV);
    }

    // Custom HTTP Headers
    httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');

    // Check platform
    if (!isPlatformBrowser(this.platformId)) {
      // Set a timeout on the server to prevent the response from taking too long
      httpHeaders = httpHeaders.set(
        CustomHttpHeaders.X_TIMEOUT,
        `${dataFiles[fileType].proxy.timeout}`,
      );
    }

    // HTTP Options
    const httpOptions = {
      headers: httpHeaders,
    };

    // Make the HTTP Request
    return this.http.get<T>(`${dataFiles[fileType].proxy.path}${fileName}`, httpOptions).pipe(
      map((response) => {
        // Set the state
        this.state.set<T>(STATE_KEY, response);

        return response;
      }),
      retry(retries),
    );
  }
}
